import { useState } from 'react';
import { Search, RotateCcw, Trash2, Clock, FileText, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAppStore } from '@/lib/store';

interface CompostHeapProps {
  className?: string;
}

export function CompostHeap({ className }: CompostHeapProps) {
  const {
    activeNovelId,
    novels,
    restoreFromCompost,
    deleteFromCompost,
    themeProfile
  } = useAppStore();

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedItem, setSelectedItem] = useState<string | null>(null);

  const activeNovel = novels.find(n => n.id === activeNovelId);
  const compost = activeNovel?.compost || [];

  const filteredCompost = compost.filter(item =>
    item.content.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.chapterTitle.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (days === 0) {
      const hours = Math.floor(diff / (1000 * 60 * 60));
      if (hours === 0) {
        const minutes = Math.floor(diff / (1000 * 60));
        return minutes === 0 ? 'Just now' : `${minutes}m ago`;
      }
      return `${hours}h ago`;
    }
    if (days === 1) return 'Yesterday';
    if (days < 7) return `${days} days ago`;
    return date.toLocaleDateString();
  };

  const handleRestore = (id: string) => {
    restoreFromCompost(id);
    setSelectedItem(null);
  };

  const handleDelete = (id: string) => {
    deleteFromCompost(id);
    setSelectedItem(null);
  };

  return (
    <div
      className={cn('flex flex-col h-full', className)}
      style={{ backgroundColor: themeProfile.background }}
    >
      {/* Header */}
      <div
        className="h-14 flex items-center justify-between px-6"
        style={{ borderBottom: `1px solid ${themeProfile.border}` }}
      >
        <div className="flex items-center gap-3">
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center"
            style={{ backgroundColor: themeProfile.border }}
          >
            <Trash2 className="w-4 h-4" style={{ color: themeProfile.text }} />
          </div>
          <div>
            <h2 className="font-semibold" style={{ color: themeProfile.text }}>Compost Heap</h2>
            <p className="text-xs" style={{ color: themeProfile.text, opacity: 0.6 }}>Deleted text is preserved here</p>
          </div>
        </div>
        <div className="text-sm" style={{ color: themeProfile.text, opacity: 0.6 }}>
          {compost.length} items · {compost.reduce((sum, item) => sum + item.wordCount, 0)} words
        </div>
      </div>

      {/* Search */}
      <div
        className="px-6 py-4"
        style={{ borderBottom: `1px solid ${themeProfile.border}` }}
      >
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: themeProfile.text, opacity: 0.5 }} />
          <input
            type="text"
            placeholder="Search deleted text..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 rounded-lg text-sm outline-none"
            style={{
              backgroundColor: themeProfile.paperSurface,
              border: `1px solid ${themeProfile.border}`,
              color: themeProfile.text
            }}
          />
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6">
        {filteredCompost.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full" style={{ color: themeProfile.accent }}>
            <Trash2 className="w-12 h-12 mb-4 opacity-20" />
            <p className="text-lg font-medium">No deleted text</p>
            <p className="text-sm">Items you delete will appear here</p>
          </div>
        ) : (
          <div className="space-y-3 max-w-3xl">
            {filteredCompost.map((item) => (
              <div
                key={item.id}
                className={cn(
                  'group p-4 rounded-xl transition-all duration-200',
                  selectedItem === item.id && 'ring-2'
                )}
                style={{
                  backgroundColor: themeProfile.paperSurface,
                  border: selectedItem === item.id
                    ? `1px solid ${themeProfile.accent}`
                    : `1px solid ${themeProfile.border}`,
                  boxShadow: selectedItem === item.id ? '0 4px 12px rgba(0,0,0,0.08)' : 'none'
                }}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div
                      className="w-8 h-8 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: themeProfile.border }}
                    >
                      <FileText className="w-4 h-4" style={{ color: themeProfile.accent }} />
                    </div>
                    <div>
                      <p className="font-medium text-sm" style={{ color: themeProfile.text }}>{item.chapterTitle}</p>
                      <div className="flex items-center gap-2 text-xs" style={{ color: themeProfile.accent }}>
                        <Clock className="w-3 h-3" />
                        <span>{formatDate(item.deletedAt)}</span>
                        <span>·</span>
                        <span>{item.wordCount} words</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={() => handleRestore(item.id)}
                      className="p-2 rounded-lg transition-colors"
                      style={{ color: themeProfile.accent }}
                      title="Restore"
                    >
                      <RotateCcw className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(item.id)}
                      className="p-2 rounded-lg transition-colors"
                      style={{ color: '#ef4444' }}
                      title="Delete permanently"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setSelectedItem(selectedItem === item.id ? null : item.id)}
                      className="p-2 rounded-lg transition-colors"
                      style={{
                        color: themeProfile.accent,
                        backgroundColor: selectedItem === item.id ? themeProfile.border : 'transparent'
                      }}
                      title="View"
                    >
                      {selectedItem === item.id ? <X className="w-4 h-4" /> : <FileText className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                {/* Content Preview */}
                <div
                  className="mt-3 p-4 rounded-lg text-sm leading-relaxed editor-richtext"
                  style={{
                    backgroundColor: themeProfile.border,
                    color: themeProfile.text
                  }}
                  dangerouslySetInnerHTML={{ __html: item.content }}
                />

                {!selectedItem && (
                  <div
                    className="text-sm line-clamp-2 prose prose-sm dark:prose-invert max-w-none opacity-60"
                    style={{ color: themeProfile.text }}
                    dangerouslySetInnerHTML={{ __html: item.content }}
                  />
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      {compost.length > 0 && (
        <div
          className="px-6 py-3"
          style={{
            borderTop: `1px solid ${themeProfile.border}`,
            backgroundColor: themeProfile.border + '40'
          }}
        >
          <p className="text-xs text-center" style={{ color: themeProfile.accent }}>
            Items are kept indefinitely. Restore to bring text back to your manuscript.
          </p>
        </div>
      )}
    </div>
  );
}

export default CompostHeap;
